import { t } from '@/i18n';
import type { Opinion } from '@/types';
import { LLMError } from './types';

/** Extracts the chatResponse contract (doc §8.3) from raw model output; tolerant of fences and stray prose. */
export function parseChatResponse(raw: string): Opinion[] {
  const trimmed = raw.trim();
  const candidates = new Set<string>([trimmed]);
  const fenced = /```(?:json)?\s*([\s\S]*?)```/i.exec(trimmed)?.[1]?.trim();
  if (fenced) candidates.add(fenced);
  const first = trimmed.indexOf('{');
  const last = trimmed.lastIndexOf('}');
  if (first >= 0 && last > first) candidates.add(trimmed.slice(first, last + 1));

  for (const candidate of candidates) {
    try {
      const opinions = normalize(JSON.parse(candidate));
      if (opinions) return opinions;
    } catch {
      // try the next extraction strategy
    }
  }
  throw new LLMError('bad_json', t('llm.badJson'));
}

function normalize(value: unknown): Opinion[] | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const list = (value as { opinions?: unknown }).opinions;
  if (!Array.isArray(list) || list.length === 0) return undefined;
  const out: Opinion[] = [];
  for (const item of list) {
    if (!item || typeof item !== 'object') return undefined;
    const o = item as Record<string, unknown>;
    const statement = typeof o.statement === 'string' ? o.statement.trim() : '';
    let confidence = typeof o.confidence === 'number' ? o.confidence : typeof o.confidence === 'string' ? Number.parseFloat(o.confidence) : Number.NaN;
    if (!statement || Number.isNaN(confidence)) return undefined;
    if (confidence > 1 && confidence <= 100) confidence /= 100; // model answered in percent
    confidence = Math.min(1, Math.max(0, confidence));
    const analysis = typeof o.analysis === 'string' ? o.analysis.trim() : '';
    out.push({ statement, confidence, analysis });
  }
  return out.sort((a, b) => b.confidence - a.confidence);
}
